import { useState, useEffect, useCallback } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  
  // Check if installed on mount
  const isInstalled = typeof window !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches;

  useEffect(() => {
    if (isInstalled) return;

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a delay (don't interrupt the user immediately)
      const hasDeclined = localStorage.getItem('pwa-install-declined');
      const lastPrompt = localStorage.getItem('pwa-install-last-prompt');
      const daysSincePrompt = lastPrompt ? (Date.now() - parseInt(lastPrompt)) / (1000 * 60 * 60 * 24) : 999;
      
      if (!hasDeclined || daysSincePrompt > 7) {
        setTimeout(() => setShowPrompt(true), 30000); // Show after 30 seconds
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for successful installation
    const handleAppInstalled = () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    // PWA install handled
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-declined', 'true');
    localStorage.setItem('pwa-install-last-prompt', Date.now().toString());
  };

  // Don't show if installed or no prompt available
  if (isInstalled || (!deferredPrompt && !isIOS) || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-24 left-4 right-4 md:left-auto md:right-6 md:w-96 z-50 animate-fade-in-up">
      <div className="bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#6B8E6B] to-[#4A6B4A] p-4 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Smartphone className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Install CampYid</h3>
                <p className="text-white/80 text-sm">Add to your home screen</p>
              </div>
            </div>
            <button 
              onClick={handleDismiss}
              className="text-white/70 hover:text-white p-1"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {isIOS ? (
            // iOS instructions
            <div className="space-y-3">
              <p className="text-[#2D5A3D] text-sm">
                Install this app on your iPhone:
              </p>
              <ol className="text-sm text-[#2D5A3D] space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#3D6B4A]/10 text-[#2D5A3D] flex items-center justify-center text-xs font-bold">1</span>
                  Tap the Share button <span className="inline-block w-5 h-5 bg-[#3D6B4A]/10 rounded" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'%232D6A4F\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z\'/%3E%3C/svg%3E")'}}></span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#3D6B4A]/10 text-[#2D5A3D] flex items-center justify-center text-xs font-bold">2</span>
                  Scroll and tap &quot;Add to Home Screen&quot;
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#3D6B4A]/10 text-[#2D5A3D] flex items-center justify-center text-xs font-bold">3</span>
                  Tap &quot;Add&quot; to install
                </li>
              </ol>
              <Button 
                onClick={handleDismiss}
                variant="outline"
                className="w-full mt-2"
              >
                Got it
              </Button>
            </div>
          ) : (
            // Android/Desktop install button
            <div className="space-y-3">
              <p className="text-[#2D5A3D] text-sm">
                Get quick access to CampYid from your home screen. No app store needed!
              </p>
              <ul className="text-xs text-[#2D5A3D] space-y-1">
                <li className="flex items-center gap-2">
                  <span className="text-[#2D5A3D]">✓</span> Works offline
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#2D5A3D]">✓</span> Faster loading
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#2D5A3D]">✓</span> Full screen experience
                </li>
              </ul>
              <div className="flex gap-2 pt-2">
                <Button 
                  onClick={handleDismiss}
                  variant="outline"
                  className="flex-1"
                >
                  Not now
                </Button>
                <Button 
                  onClick={handleInstall}
                  className="flex-1 btn-primary flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Install
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
